import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { useLang } from '../../lib/lang';
import { adminListTextOverrides, adminUpsertTextOverride, adminDeleteTextOverride, adminTranslate } from '../../lib/api';
import translations from '../../lib/i18n';
import AdminSidebar from '../../components/AdminSidebar';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Pencil, Search, Sparkles, Save, RotateCcw, Loader2, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';

export default function AdminTextOverrides() {
  const { user, isAdmin, loading } = useAuth();
  const { refreshOverrides } = useLang();
  const navigate = useNavigate();
  const [overrides, setOverrides] = useState({});
  const [search, setSearch] = useState('');
  const [editKey, setEditKey] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user || !isAdmin) { navigate('/admin/login'); return; }
    loadOverrides();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isAdmin, loading]);

  const loadOverrides = async () => {
    try {
      const res = await adminListTextOverrides();
      const map = {};
      (res.data?.overrides || []).forEach((o) => { map[o.key] = o; });
      setOverrides(map);
    } catch {
      toast.error('Erreur de chargement');
    }
  };

  // Build list of all i18n keys with their default FR/EN values + override status
  const allKeys = Object.keys(translations.en || {});
  const filteredKeys = allKeys.filter((k) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      k.toLowerCase().includes(s) ||
      (translations.en?.[k] || '').toLowerCase().includes(s) ||
      (translations.fr?.[k] || '').toLowerCase().includes(s)
    );
  });

  const overrideStats = {
    total: allKeys.length,
    overridden: Object.keys(overrides).length,
  };

  return (
    <div className="min-h-screen flex bg-cream">
      <AdminSidebar />
      <main className="flex-1 p-6 lg:p-10 overflow-x-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="font-display text-3xl font-bold text-rolex-green mb-1">Textes du Site</h1>
            <p className="text-rolex-green/65 text-sm">
              Modifiez n'importe quel texte affiché sur le site (boutons, titres, descriptions). Vous pouvez aussi activer le mode «&nbsp;Édition&nbsp;» depuis le header pour modifier directement les textes en parcourant le site.
            </p>
          </div>

          <div className="grid grid-cols-3 gap-4 mb-6">
            <Card className="border-2 border-gold/30 bg-white">
              <CardContent className="p-4">
                <p className="text-[10px] uppercase tracking-wider text-rolex-green/60">Total clés</p>
                <p className="font-mono text-2xl font-bold text-rolex-green">{overrideStats.total}</p>
              </CardContent>
            </Card>
            <Card className="border-2 border-gold/40 bg-gold-gradient/10">
              <CardContent className="p-4">
                <p className="text-[10px] uppercase tracking-wider text-rolex-green/60">Personnalisés</p>
                <p className="font-mono text-2xl font-bold text-gold-dark">{overrideStats.overridden}</p>
              </CardContent>
            </Card>
            <Card className="border-2 border-emerald-300 bg-emerald-50">
              <CardContent className="p-4">
                <p className="text-[10px] uppercase tracking-wider text-rolex-green/60">% Couverture</p>
                <p className="font-mono text-2xl font-bold text-emerald-700">{overrideStats.total ? Math.round((overrideStats.overridden / overrideStats.total) * 100) : 0}%</p>
              </CardContent>
            </Card>
          </div>

          <Card className="bg-white border-2 border-gold/30 shadow-lg">
            <CardHeader className="border-b border-gold/20">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <CardTitle className="text-rolex-green flex items-center gap-2">
                  <Pencil className="w-5 h-5 text-gold-dark" />
                  Éditer les textes
                </CardTitle>
                <div className="relative w-full sm:w-72">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-rolex-green/45" />
                  <Input
                    placeholder="Rechercher une clé ou un texte…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9 border-gold/30"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0 max-h-[70vh] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-cream/60 sticky top-0 z-10">
                  <tr className="border-b border-gold/20">
                    <th className="text-left px-4 py-2 font-semibold text-rolex-green/80 text-xs uppercase tracking-wide">Clé</th>
                    <th className="text-left px-4 py-2 font-semibold text-rolex-green/80 text-xs uppercase tracking-wide">🇫🇷 FR</th>
                    <th className="text-left px-4 py-2 font-semibold text-rolex-green/80 text-xs uppercase tracking-wide">🇬🇧 EN</th>
                    <th className="text-left px-4 py-2 font-semibold text-rolex-green/80 text-xs uppercase tracking-wide w-24">Statut</th>
                    <th className="px-4 py-2 w-20"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredKeys.slice(0, 500).map((key) => {
                    const ov = overrides[key];
                    const fr = ov?.value_fr || translations.fr?.[key] || '';
                    const en = ov?.value_en || translations.en?.[key] || '';
                    const isOverridden = !!ov;
                    return (
                      <tr key={key} className="border-b border-gold/10 hover:bg-cream/40 transition-colors">
                        <td className="px-4 py-2 font-mono text-[11px] text-gold-dark whitespace-nowrap">{key}</td>
                        <td className="px-4 py-2 max-w-[280px] truncate text-rolex-green" title={fr}>{fr}</td>
                        <td className="px-4 py-2 max-w-[280px] truncate text-rolex-green" title={en}>{en}</td>
                        <td className="px-4 py-2">
                          {isOverridden ? (
                            <Badge className="bg-gold/20 text-gold-dark border-gold/40">Custom</Badge>
                          ) : (
                            <Badge variant="outline" className="text-rolex-green/55">Default</Badge>
                          )}
                        </td>
                        <td className="px-4 py-2">
                          <Button size="sm" variant="ghost" onClick={() => setEditKey(key)} className="h-8 w-8 p-0 text-gold-dark hover:bg-gold/10" data-testid={`edit-key-${key}`}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                  {filteredKeys.length === 0 && (
                    <tr><td colSpan={5} className="text-center py-12 text-rolex-green/55">Aucun résultat</td></tr>
                  )}
                </tbody>
              </table>
              {filteredKeys.length > 500 && (
                <p className="p-3 text-xs text-rolex-green/55 text-center bg-cream/40">Affichage limité à 500 résultats. Affinez votre recherche.</p>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {editKey && (
        <EditOverrideDialog
          tKey={editKey}
          existing={overrides[editKey]}
          onClose={() => setEditKey(null)}
          onSaved={async () => {
            await loadOverrides();
            await refreshOverrides();
          }}
        />
      )}
    </div>
  );
}

function EditOverrideDialog({ tKey, existing, onClose, onSaved }) {
  const [valueFr, setValueFr] = useState(existing?.value_fr || translations.fr?.[tKey] || '');
  const [valueEn, setValueEn] = useState(existing?.value_en || translations.en?.[tKey] || '');
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(null);
  const isLong = (translations.en?.[tKey] || '').length > 60;

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminUpsertTextOverride({ key: tKey, value_fr: valueFr, value_en: valueEn });
      toast.success('Sauvegardé');
      onSaved();
      onClose();
    } catch {
      toast.error('Erreur de sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Restaurer les valeurs par défaut ?')) return;
    setSaving(true);
    try {
      await adminDeleteTextOverride(tKey);
      toast.success('Restauré');
      onSaved();
      onClose();
    } catch {
      toast.error('Erreur');
    } finally {
      setSaving(false);
    }
  };

  const doTranslate = async (direction) => {
    const isFrToEn = direction === 'fr-to-en';
    const text = isFrToEn ? valueFr : valueEn;
    if (!text.trim()) return toast.error('Champ source vide');
    setTranslating(direction);
    try {
      const res = await adminTranslate(text, isFrToEn ? 'fr' : 'en', isFrToEn ? 'en' : 'fr', `WINLUXOR UI key="${tKey}"`);
      const tr = res.data?.translation || '';
      if (isFrToEn) setValueEn(tr); else setValueFr(tr);
      toast.success(res.data?.cached ? 'Cache' : `Traduit (${res.data?.tokens_used || '?'}t)`);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 402) toast.error('OpenAI: crédit épuisé');
      else toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setTranslating(null);
    }
  };

  const Comp = isLong ? Textarea : Input;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl bg-cream border-2 border-gold/40">
        <DialogHeader>
          <DialogTitle className="text-rolex-green flex items-center gap-2">
            <Pencil className="w-5 h-5 text-gold-dark" /> Éditer
            <span className="font-mono text-sm text-gold-dark">{tKey}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>🇫🇷 Français</Label>
              <Button size="sm" variant="ghost" disabled={translating !== null || !valueEn.trim()} onClick={() => doTranslate('en-to-fr')} className="text-xs h-7">
                {translating === 'en-to-fr' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} EN → FR
              </Button>
            </div>
            <Comp value={valueFr} onChange={(e) => setValueFr(e.target.value)} className="bg-white border-gold/30" rows={isLong ? 4 : undefined} />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>🇬🇧 English</Label>
              <Button size="sm" variant="ghost" disabled={translating !== null || !valueFr.trim()} onClick={() => doTranslate('fr-to-en')} className="text-xs h-7">
                {translating === 'fr-to-en' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} FR → EN
              </Button>
            </div>
            <Comp value={valueEn} onChange={(e) => setValueEn(e.target.value)} className="bg-white border-gold/30" rows={isLong ? 4 : undefined} />
          </div>
        </div>
        <DialogFooter className="flex flex-row justify-between gap-2">
          <Button variant="outline" disabled={saving || !existing} onClick={handleReset} className="text-red-600 border-red-300 hover:bg-red-50">
            <RotateCcw className="w-4 h-4" /> Restaurer défaut
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={saving}><X className="w-4 h-4" /> Annuler</Button>
            <Button onClick={handleSave} disabled={saving} className="btn-gold">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Sauvegarder
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
